import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

export const getOperationsDashboard = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => z.object({ range: z.enum(["24h", "7d", "30d"]).default("7d") }).parse(input))
  .handler(async ({ data, context }) => {
    const { data: isAdmin, error: roleError } = await context.supabase.rpc("has_role", {
      _user_id: context.userId,
      _role: "admin",
    });
    if (roleError || !isAdmin) throw new Error("Forbidden: admin only");

    const hours = data.range === "24h" ? 24 : data.range === "7d" ? 168 : 720;
    const since = new Date(Date.now() - hours * 3_600_000).toISOString();
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: rows, error } = await supabaseAdmin
      .from("operations_events")
      .select("id, category, operation, outcome, severity, status_code, latency_ms, correlation_id, metadata, created_at")
      .gte("created_at", since)
      .order("created_at", { ascending: false })
      .limit(10000);
    if (error) throw new Error(error.message);

    const events = rows ?? [];
    const count = (predicate: (event: (typeof events)[number]) => boolean) => events.filter(predicate).length;
    const failures = count((event) => event.severity === "error" || event.severity === "critical");
    const warnings = count((event) => event.severity === "warning");
    const quota = events.filter((event) => event.category === "quota");
    const reservations = events.filter((event) => event.category === "rate_limit");
    const jobs = events.filter((event) => event.category === "webhook" || event.category === "cron");
    const completedLatencies = events.map((event) => event.latency_ms).filter((value): value is number => typeof value === "number");
    completedLatencies.sort((a, b) => a - b);
    const p95 = completedLatencies.length ? completedLatencies[Math.min(completedLatencies.length - 1, Math.floor(completedLatencies.length * 0.95))] : null;

    const bucketMs = data.range === "24h" ? 3_600_000 : data.range === "7d" ? 86_400_000 : 86_400_000;
    const bucketCount = Math.ceil((hours * 3_600_000) / bucketMs);
    const timeline = Array.from({ length: bucketCount }, (_, index) => {
      const start = Date.now() - (bucketCount - index) * bucketMs;
      const end = start + bucketMs;
      const bucket = events.filter((event) => {
        const time = new Date(event.created_at).getTime();
        return time >= start && time < end;
      });
      return {
        at: new Date(start).toISOString(),
        total: bucket.length,
        failures: bucket.filter((event) => event.severity === "error" || event.severity === "critical").length,
        denied: bucket.filter((event) => event.outcome === "denied" || event.outcome === "limited").length,
      };
    });

    const alerts = events
      .filter((event) => event.severity === "error" || event.severity === "critical" || event.severity === "warning")
      .slice(0, 50);

    const categorySummary = ["quota", "webhook", "cron", "rate_limit", "quick_scan"].map((category) => {
      const categoryEvents = events.filter((event) => event.category === category);
      const categoryFailures = categoryEvents.filter((event) => event.severity === "error" || event.severity === "critical").length;
      return {
        category,
        total: categoryEvents.length,
        failures: categoryFailures,
        health: categoryFailures > 0 ? "degraded" : categoryEvents.length > 0 ? "healthy" : "no-data",
      };
    });

    return {
      range: data.range,
      generatedAt: new Date().toISOString(),
      totals: {
        events: events.length,
        failures,
        warnings,
        p95LatencyMs: p95,
        quotaAllowed: quota.filter((event) => event.outcome.startsWith("allowed")).length,
        quotaDenied: quota.filter((event) => event.outcome.startsWith("denied")).length,
        reservationsAllowed: reservations.filter((event) => event.outcome === "reserved").length,
        reservationsLimited: reservations.filter((event) => event.outcome === "limited").length,
        jobFailures: jobs.filter((event) => event.severity === "error" || event.severity === "critical").length,
      },
      timeline,
      categorySummary,
      alerts,
      recent: events.slice(0, 100),
      truncated: events.length === 10000,
    };
  });